import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, LogOut, Hammer, Cpu, Truck, Award, ThumbsUp, Bell, Sun, Moon, Users2, MessageSquare, User as UserIcon, UserPlus, HeartHandshake } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { listEndorsementRequests, getUnreadCount, pendingConnectionCount } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const categoryIcon = (category, className = "w-4 h-4") => {
  if (category === "Carpentry") return <Hammer className={className} />;
  if (category === "Digital Tasks") return <Cpu className={className} />;
  if (category === "Logistics") return <Truck className={className} />;
  return <Award className={className} />;
};

const ConnectionBell = () => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const { subscribe } = useApp();
  const load = useCallback(() => pendingConnectionCount().then((r) => setCount(r.pending || 0)).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const unsub = subscribe((ev) => {
      if (ev.type === "connection_request") setCount((c) => c + 1);
      if (ev.type === "connection_resolved") load();
    });
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); unsub(); };
  }, [load, subscribe]);
  return (
    <button data-testid="connection-bell" onClick={() => navigate("/network?tab=pending")}
            aria-label={count > 0 ? `${count} pending connection requests` : "No pending connections"}
            className="relative w-10 h-10 rounded-full bg-secondary text-foreground hover:bg-primary/10 hover:text-primary flex items-center justify-center transition-colors">
      <UserPlus className="w-5 h-5" />
      {count > 0 && (
        <motion.span key={count} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="connection-bell-count"
                     className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center border-2 border-background">
          {count > 9 ? "9+" : count}
        </motion.span>
      )}
    </button>
  );
};

const NotificationBell = ({ employerId }) => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    let mounted = true;
    const load = () => listEndorsementRequests({ employer_id: employerId, status: "pending" })
      .then((r) => mounted && setCount(r.length)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { mounted = false; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [employerId]);
  return (
    <button
      data-testid="notif-bell"
      onClick={() => navigate("/employer?tab=requests")}
      aria-label={count > 0 ? `${count} pending endorsement requests` : "No pending requests"}
      className="relative w-10 h-10 rounded-full bg-secondary text-foreground hover:bg-primary/10 hover:text-primary flex items-center justify-center transition-colors"
    >
      <Bell className="w-5 h-5" />
      {count > 0 && (
        <motion.span key={count} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="notif-bell-count"
                     className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center border-2 border-background">
          {count > 9 ? "9+" : count}
        </motion.span>
      )}
    </button>
  );
};

const NavItem = ({ to, icon, label, active, badge, testid, onClick }) => (
  <button data-testid={testid} onClick={onClick}
    className={`relative inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${active ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"}`}>
    {icon}
    <span className="hidden sm:inline">{label}</span>
    {badge > 0 && (
      <span className="absolute -top-1 -right-1 sm:static sm:ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
        {badge > 9 ? "9+" : badge}
      </span>
    )}
  </button>
);

export const Header = ({ title, right }) => {
  const { currentUser, logout, theme, toggleTheme, subscribe } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    let mounted = true;
    const load = () => getUnreadCount().then((r) => mounted && setUnread(r.unread || 0)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const unsub = subscribe?.((ev) => {
      if (ev.type === "message" && ev.message?.to_user_id === currentUser.id) {
        // only increment if we are not currently on that thread
        if (!location.pathname.includes(`/messages/${ev.message.from_user_id}`)) {
          setUnread((n) => n + 1);
        }
      }
    });
    return () => { mounted = false; clearInterval(id); window.removeEventListener("focus", onFocus); unsub?.(); };
  }, [currentUser, location.pathname, subscribe]);

  const isActive = (p) => location.pathname.startsWith(p);
  const homePath = currentUser?.role === "employer" ? "/employer" : "/worker";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <button data-testid="brand-home-btn" onClick={() => navigate(currentUser ? homePath : "/")}
                className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-sm group-active:scale-95 transition-transform">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="text-left leading-none">
            <div className="font-heading font-extrabold text-lg tracking-tight text-foreground">SkillBridge</div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">{title}</div>
          </div>
        </button>

        {currentUser && (
          <nav className="hidden md:flex items-center gap-1">
            <NavItem testid="nav-home" icon={<ShieldCheck className="w-4 h-4" />} label="Home" active={isActive(homePath)} onClick={() => navigate(homePath)} />
            <NavItem testid="nav-network" icon={<Users2 className="w-4 h-4" />} label="Network" active={isActive("/network")} onClick={() => navigate("/network")} />
            <NavItem testid="nav-messages" icon={<MessageSquare className="w-4 h-4" />} label="Messages" active={isActive("/messages")} badge={unread} onClick={() => navigate("/messages")} />
            <NavItem testid="nav-communities" icon={<HeartHandshake className="w-4 h-4" />} label="Communities" active={isActive("/communities")} onClick={() => navigate("/communities")} />
            <NavItem testid="nav-recommendations" icon={<Award className="w-4 h-4" />} label="Recommendations" active={isActive("/recommendations")} onClick={() => navigate("/recommendations")} />
          </nav>
        )}

        <div className="flex items-center gap-2">
          {right}
          {currentUser && <ConnectionBell />}
          {currentUser?.role === "employer" && <NotificationBell employerId={currentUser.id} />}
          <button data-testid="theme-toggle" onClick={toggleTheme}
                  aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                  className="w-10 h-10 rounded-full bg-secondary text-foreground hover:bg-primary/10 hover:text-primary flex items-center justify-center transition-colors">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          {currentUser ? (
            <button data-testid="header-account-btn" onClick={() => navigate("/account")} className="hidden sm:block">
              <Avatar className="w-9 h-9 border border-border">
                <AvatarImage src={currentUser.avatar_url} className="object-cover" />
                <AvatarFallback>{currentUser.name?.[0]}</AvatarFallback>
              </Avatar>
            </button>
          ) : (
            <Button data-testid="header-signin-btn" size="sm" variant="ghost" onClick={() => navigate("/login")}>Sign in</Button>
          )}
          {currentUser && (
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/"); }} className="text-muted-foreground md:hidden">
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      {currentUser && (
        <nav className="md:hidden flex items-center gap-1 px-2 pb-2 overflow-x-auto">
          <NavItem testid="nav-home-m" icon={<ShieldCheck className="w-4 h-4" />} label="Home" active={isActive(homePath)} onClick={() => navigate(homePath)} />
          <NavItem testid="nav-network-m" icon={<Users2 className="w-4 h-4" />} label="Network" active={isActive("/network")} onClick={() => navigate("/network")} />
          <NavItem testid="nav-messages-m" icon={<MessageSquare className="w-4 h-4" />} label="Messages" active={isActive("/messages")} badge={unread} onClick={() => navigate("/messages")} />
          <NavItem testid="nav-communities-m" icon={<HeartHandshake className="w-4 h-4" />} label="Communities" active={isActive("/communities")} onClick={() => navigate("/communities")} />
          <NavItem testid="nav-recommendations-m" icon={<Award className="w-4 h-4" />} label="Recs" active={isActive("/recommendations")} onClick={() => navigate("/recommendations")} />
          <NavItem testid="nav-account-m" icon={<UserIcon className="w-4 h-4" />} label="Account" active={isActive("/account")} onClick={() => navigate("/account")} />
        </nav>
      )}
    </header>
  );
};

export const SkillTag = ({ skill, testid }) => (
  <div data-testid={testid}
       className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground">
    {skill.verified_status === "verified" && <ShieldCheck className="w-3 h-3 text-primary shrink-0" />}
    <span>{skill.skill_name}</span>
    <span className="flex gap-0.5" aria-label={`level ${skill.proficiency_level} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`w-1 h-1 rounded-full ${n <= skill.proficiency_level ? "bg-primary" : "bg-border"}`} />
      ))}
    </span>
    {skill.endorsement_count > 0 && (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-primary">
        <ThumbsUp className="w-2.5 h-2.5" />{skill.endorsement_count}
      </span>
    )}
  </div>
);

export const StatCard = ({ label, value, icon, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-border bg-card p-4">
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-muted-foreground">{label}</span>
      <span className="text-primary">{icon}</span>
    </div>
    <div className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-foreground">{value}</div>
  </div>
);

export const CredentialBadge = ({ badge, index = 0 }) => {
  const navigate = useNavigate();
  return (
    <motion.button
      type="button"
      onClick={() => navigate(`/verify/${badge.public_verify_hash}`)}
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }} data-testid={`credential-badge-${index}`}
      aria-label={`Verify ${badge.badge_title}`}
      className="group flex flex-col items-center text-center w-36 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg p-2"
    >
      <div className="relative w-14 h-14 transition-transform group-hover:-translate-y-0.5">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary to-blue-700 shadow-md" />
        <div className="absolute inset-[3px] rounded-full border border-white/20" />
        <div className="absolute inset-0 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.4} /></div>
      </div>
      <div className="mt-2.5 text-xs font-semibold leading-tight text-foreground">{badge.badge_title}</div>
      <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">#{badge.public_verify_hash}</div>
      <div className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-semibold tracking-[0.15em] uppercase text-primary">
        Verify →
      </div>
    </motion.button>
  );
};
